import AddButton from "@components/AddButton/AddButton";
import EmptyList from "@components/EmptyList/EmptyList";
import Header from "@components/Header/Header";
import SectionLabel from "@components/SectionLabel/SectionLabel";
import AccountListItem from "@components/AccountListItem/AccountListItem";
import { PALE_BLUE } from "@styles/colors";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
    Container,
    BodyContainer,
    ViewModeContainer,
    EmptyListContainer,
    EmptyListText,
    BankAccountItemContainer,
    BottomButtonContainer
} from "./ManageBank.styles";
import { BankForm } from "./Form";

import useBankManagement from "@hooks/useManageBank";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import type { GetBankingResponse } from "../../types/redemption";
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon";
import SuccessModal from '@components/SuccessModal/SuccessModal';
import { ApiError } from "@lib/api";
import Snackbar from "@components/Snackbar/Snackbar";
import DialogConfirmationBox from "@components/DialogConfirmationBox/DialogConfirmationBox";
import { useUserRestrictions } from "@hooks/useUserRestrictions";

const ManageBank = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const {
        banks,
        isLoading: isLoadingBanks,
        supportedBanks,
        addBank,
        editBank,
        removeBank,
        validateBank,
        reload,
    } = useBankManagement()

    const { canManageBankAccounts } = useUserRestrictions()

    const [successOpen, setSuccessOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [verificationModalVisible, setVerificationModalVisible] = useState(false)
    const [failedModalVisible, setFailedModalVisible] = useState(false)
    const [isError, setIsError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('');
    const [isLocalLoading, setIsLocalLoading] = useState(false)
    const [isConfirmationDialogVisible, setIsConfirmationDialogVisible] = useState(false)
    const [toBeRemoved, setToBeRemoved] = useState('')

    // Check for mode and source in URL query params
    const queryParams = new URLSearchParams(location.search);
    const rawMode = queryParams.get('mode');
    const initialMode = rawMode === 'view' || rawMode === 'edit' || rawMode === 'add' ? rawMode : 'view';
    const source = queryParams.get('source');

    const [mode, setMode] = useState<'view' | 'edit' | 'add'>(initialMode);
    const [selectedBank, setSelectedBank] = useState<GetBankingResponse | null>(null);

    const currentAmount = queryParams.get('amount')
    const parsedAmount = currentAmount ? Number(currentAmount) : undefined
    const validAmount = parsedAmount && !isNaN(parsedAmount) && parsedAmount > 0 ? parsedAmount : undefined

    const onBackClick = async (uuid?: string) => {
        if (source === 'redeem-bank') {
            navigate('/redeem-bank', {
                state: {
                    bank_uuid: uuid,
                    amount: validAmount
                }
            });
            return;
        }
        if (mode === 'edit' || mode === 'add') {
            await reload()
            setMode('view');
            setSelectedBank(null);
            setIsError(false)
            setErrorMessage('')
        } else {
            navigate('/bank-and-address')
        }
    };

    const handleBankClick = (bank: GetBankingResponse) => {
        setSelectedBank(bank);
        setMode('edit');
    };

    const handleAddNewClick = () => {
        setSelectedBank(null);
        setMode('add');
    };

    const handlePromptConfirmation = async (uuid: string) => {
        setIsConfirmationDialogVisible(true)
        setToBeRemoved(uuid)
    }

    const handleHideConfirmation = async () => {
        setIsConfirmationDialogVisible(false)
    }

    const handleAddBankOperation = async (
        account_nickname: string,
        bank_name: string,
        bank_account: string,
        revalidate: boolean = false,
        bank_uuid?: string,
    ) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.addedSuccessfully'));
        try {
            let response
            if (revalidate && bank_uuid) {
                response = await validateBank({ bank_uuid })
            } else {
                response = await addBank({
                    account_nickname,
                    bank_name,
                    bank_account,
                    asset: "idr",
                    source: "REDEMPTION",
                })
            }
            if (response.status === 'VALIDATED') {
                setSuccessOpen(true);
                setTimeout(async () => {
                    onBackClick(response.bank_uuid)
                }, 2000)
            } else if (response.status === 'INVALID') {
                setFailedModalVisible(true);
            } else if (response.status === 'VALIDATION_REQUESTED') {
                if (bank_uuid && revalidate) {
                    setVerificationModalVisible(true);
                } else {
                    await handleAddBankOperation(account_nickname, bank_name, bank_account, true, response.bank_uuid)
                }
            }
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific banking restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.bankDisabled'))
                    return
                }

                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)
                const errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
                setErrorMessage(errorMessage)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleEditBankOperation = async (
        account_nickname: string,
        bank_name: string,
        bank_account: string,
        revalidate: boolean = false,
        bank_uuid: string,
    ) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.updatedSuccessfully'));
        try {
            let response
            if (revalidate && bank_uuid) {
                response = await validateBank({ bank_uuid })
            } else {
                response = await editBank({
                    bank_uuid,
                    account_nickname,
                    bank_name,
                    bank_account,
                    asset: "idr",
                    source: "REDEMPTION",
                })
            }
            if (response.status === 'VALIDATED') {
                setSuccessOpen(true);
                setTimeout(async () => {
                    onBackClick()
                }, 2000)
            } else if (response.status === 'INVALID') {
                setFailedModalVisible(true);
            } else if (response.status === 'VALIDATION_REQUESTED') {
                if (bank_uuid && revalidate) {
                    setVerificationModalVisible(true);
                } else {
                    await handleEditBankOperation(account_nickname, bank_name, bank_account, true, response.bank_uuid)
                }
            }
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific banking restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.bankDisabled'))
                    return
                }

                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)
                const errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
                setErrorMessage(errorMessage)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleRemoveBankOperation = async (bank_uuid: string) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.removedSuccessfully'));
        try {
            await removeBank({ bank_uuid })
            setSuccessOpen(true);

            setTimeout(async () => {
                onBackClick()
            }, 2000)
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific banking restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.bankDisabled'))
                    return
                }

                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)
                const errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
                setErrorMessage(errorMessage)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleSave = async (bank: any) => {
        if (source === 'redeem-bank') {
            await handleAddBankOperation(bank.account_nickname, bank.bank_name, bank.bank_account)
        } else {
            if (selectedBank) {
                await handleEditBankOperation(bank.account_nickname, bank.bank_name, bank.bank_account, false, bank.bank_uuid)
            } else {
                await handleAddBankOperation(bank.account_nickname, bank.bank_name, bank.bank_account)
            }
        }
    };

    const handleRemove = async (uuid: string) => {
        await handleRemoveBankOperation(uuid)
    };

    return (
        <Container>
            <Header title={t('bankAndAddress.bank.header')} showBackButton onBackClick={async () => await onBackClick()} />
            {isLoadingBanks && <LoadingModal visible={isLoadingBanks} />}
            {isLocalLoading && <LoadingModal visible={isLocalLoading} />}
            <BodyContainer>
                {mode === 'view' && <ViewModeContainer>
                    <SectionLabel>{t('bankAndAddress.bank.sectionLabel')}</SectionLabel>
                    {banks && !banks.length &&
                        <EmptyListContainer>
                            <EmptyList>
                                <EmptyListText>
                                    {t('bankAndAddress.bank.emptyText')}
                                </EmptyListText>
                                {canManageBankAccounts() && <AddButton onClick={handleAddNewClick} label={t('bankAndAddress.bank.addNew')} $backgroundColor={PALE_BLUE} />}
                            </EmptyList>
                        </EmptyListContainer>}
                    {
                        banks &&
                        banks.length > 0 &&
                        !isLoadingBanks &&
                        supportedBanks && (
                            banks.map((bank, index) => {
                                if (bank.is_deleted) return null
                                return (
                                    <BankAccountItemContainer key={index}>
                                        <AccountListItem
                                            name={bank.bank_account_name}
                                            accountNumber={bank.bank_account}
                                            iconUrl={supportedBanks!.find((b) => b.code === bank.bank_name)?.icon_url || ''}
                                            provider={supportedBanks!.find((b) => b.code === bank.bank_name)?.display_name || ''}
                                            nickname={bank.account_nickname}
                                            isValid={bank.status === "VALIDATED"}
                                            tag={bank.status === "VALIDATED" && canManageBankAccounts() ? "edit" : "none"}
                                            onClick={() => handleBankClick(bank)}
                                        />
                                    </BankAccountItemContainer>
                                )
                            })
                        )}
                    {banks && banks.length !== 0 && canManageBankAccounts() && <BottomButtonContainer>
                        <AddButton onClick={handleAddNewClick} label={t('bankAndAddress.bank.addNew')} $backgroundColor={PALE_BLUE} />
                    </BottomButtonContainer>}
                </ViewModeContainer>}
                {mode === 'edit' &&
                    <BankForm
                        onRemove={handlePromptConfirmation}
                        onSave={handleSave}
                        supportedBanks={supportedBanks}
                        initialName={selectedBank?.bank_account_name}
                        initialAccountNumber={selectedBank?.bank_account}
                        initialProvider={selectedBank?.bank_name}
                        initialNickname={selectedBank?.account_nickname}
                        uuid={selectedBank?.bank_uuid}
                        errorText={errorMessage}
                        mode="edit"
                    />
                }
                {mode === 'add' &&
                    <BankForm
                        onRemove={handlePromptConfirmation}
                        onSave={handleSave}
                        supportedBanks={supportedBanks}
                        initialName={selectedBank?.bank_account_name}
                        initialAccountNumber={selectedBank?.bank_account}
                        initialProvider={selectedBank?.bank_name}
                        initialNickname={selectedBank?.account_nickname}
                        uuid={selectedBank?.bank_uuid}
                        errorText={errorMessage}
                        mode="add"
                    />
                }
            </BodyContainer>
            {successOpen && <SuccessModal
                visible={successOpen}
                message={successMessage}
                onHide={() => setSuccessOpen(false)}
            />}
            {verificationModalVisible && <ModalWithIcon
                title={t('errors.verification.inProgress.bank.title')}
                description={t('errors.verification.inProgress.bank.description')}
                onPositive={async () => {
                    setVerificationModalVisible(false)
                    onBackClick()
                }}
                positiveText={t('errors.verification.inProgress.positiveText')}
                visible={verificationModalVisible}
                type="void"
            />}
            {failedModalVisible && <ModalWithIcon
                title={t('errors.verification.failed.bank.title')}
                description={t('errors.verification.failed.bank.description')}
                onPositive={async () => {
                    setFailedModalVisible(false)
                    onBackClick()
                }}
                positiveText={t('errors.verification.failed.positiveText')}
                visible={failedModalVisible}
                type="failed"
            />}
            {isError && <Snackbar
                width='90%'
                duration={8000}
                message={errorMessage}
                isVisible={isError}
                onClose={() => setErrorMessage('')}
            />}
            {isConfirmationDialogVisible && <DialogConfirmationBox
                isOpen={isConfirmationDialogVisible}
                title={t('bankAndAddress.bank.removeConfirmation.title')}
                description={t('bankAndAddress.bank.removeConfirmation.description')}
                positiveButtonText={t('bankAndAddress.bank.removeConfirmation.positiveButtonText')}
                negativeButtonText={t('bankAndAddress.bank.removeConfirmation.negativeButtonText')}
                onPositiveClick={handleHideConfirmation}
                onNegativeClick={async () => {
                    await handleHideConfirmation()
                    await handleRemove(toBeRemoved)
                }}
                onOverlayClick={handleHideConfirmation}
            />}
        </Container>
    )
}



export default ManageBank;